import { useState } from 'react'
import { SketchPicker } from 'react-color'
import { Popover, PopoverContent, PopoverTrigger, IconButton } from '@shopflo/ui'
import { classNames, rgbHex } from 'src/lib/utils/helpers'
import { Eye, EyeSlash } from '@phosphor-icons/react'

const colorPickerStyles = `
  .color-picker-wrapper .saturation-white > div > div {
    width: 16px !important;
    height: 16px !important;
    border-radius: 50% !important;
    border: 3px solid white !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important;
    transform: translate(-8px, -8px) !important;
  }
  .color-picker-wrapper .hue-horizontal > div > div {
    min-width: 6px !important;
    height: 15px !important;
    border-radius: 12px !important;
    background: white !important;
    border: 1px solid rgba(9, 9, 11, 0.24) !important;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1) !important;
    transform: translateX(-4px) !important;
  }
  .color-picker-wrapper .flexbox-fix > div > div:nth-child(2) div[style*="margin-top: 1px"] {
    min-width: 6px !important;
    height: 15px !important;
    border-radius: 12px !important;
    background: white !important;
    border: 1px solid rgba(9, 9, 11, 0.24) !important;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1) !important;
    transform: translateX(-5px) !important;
  }

  .sketch-picker .flexbox-fix div{
    min-height: 15px !important;
  }
  .color-picker-wrapper input {
    border-radius: 6px !important;
    border: 1px solid rgba(113, 113, 122, 0.2) !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .color-picker-wrapper input:focus {
    border: 1px solid #52525B !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .color-picker-wrapper label {
    color: #71717A !important;
  }
`

const CheckerboardSwatch = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%' className='absolute inset-0'>
    <defs>
      <pattern id='checker' x='0' y='0' width='8' height='8' patternUnits='userSpaceOnUse'>
        <rect width='8' height='8' fill='#ffffff' />
        <rect x='0' y='0' width='4' height='4' fill='#d1d5db' />
        <rect x='4' y='4' width='4' height='4' fill='#d1d5db' />
      </pattern>
    </defs>
    <rect width='100%' height='100%' fill='url(#checker)' />
  </svg>
)

interface ColorPickerV2Props {
  label: string
  color: string
  onChange: (color: string) => void
  disabled?: boolean
  showVisibilityToggle?: boolean
  isVisible?: boolean
  onVisibilityChange?: (visible: boolean) => void
}

const ColorPickerV2: React.FC<ColorPickerV2Props> = ({
  label,
  color,
  onChange,
  disabled = false,
  showVisibilityToggle = false,
  isVisible = true,
  onVisibilityChange
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const handleColorChange = (colorResult: any) => {
    const hex = '#' + rgbHex(colorResult.rgb.r, colorResult.rgb.g, colorResult.rgb.b, colorResult.rgb.a).toUpperCase()
    onChange(hex)
  }

  const handleVisibilityToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    onVisibilityChange?.(!isVisible)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <div
          className={classNames(
            'flex w-full rounded-md overflow-hidden items-center gap-3 pr-2',
            'bg-zinc-950/4 hover:bg-zinc-950/8',
            'text-zinc-500 hover:text-zinc-600',
            disabled ? 'cursor-not-allowed opacity-50 bg-gray-lighter' : 'cursor-pointer'
          )}
        >
          <button
            className={classNames(
              'relative w-8 h-8 border-1 p-0 rounded-l-md border-r border-zinc-950/8 overflow-hidden'
            )}
            style={{ opacity: isVisible ? 1 : 0.6 }}
          >
            <CheckerboardSwatch />
            <div className='absolute inset-0' style={{ backgroundColor: color }} />
          </button>
          <div
            className={classNames('py-1 text-text-sm-medium flex-1', isVisible ? 'text-zinc-500' : 'text-zinc-500/60')}
          >
            {label}
          </div>
          {showVisibilityToggle && (
            <IconButton
              variant='ghost'
              size='xs'
              IconComponent={isVisible ? Eye : EyeSlash}
              iconProps={{ weight: 'regular' }}
              onClick={handleVisibilityToggle}
            />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className='bg-white w-auto p-0 !border-none !rounded-xl overflow-hidden' align='start' sideOffset={8} onOpenAutoFocus={e => e.preventDefault()}>
        <style>{colorPickerStyles}</style>
        <div className='color-picker-wrapper'>
          <SketchPicker
            presetColors={[]}
            color={color}
            onChange={handleColorChange}
            styles={{
              default: {
                picker: {
                  width: '224px',
                  padding: '12px',
                  boxShadow: 'none',
                  borderRadius: '8px'
                },
                saturation: {
                  paddingBottom: '220px',
                  borderRadius: '6px'
                },
                controls: {
                  marginTop: '8px'
                },
                hue: {
                  height: '19px',
                  borderRadius: '4px'
                },
                alpha: {
                  height: '19px',
                  borderRadius: '4px'
                },
                activeColor: {
                  display: 'none'
                },
                color: {
                  display: 'none'
                }
              }
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default ColorPickerV2
